import React, { useCallback, useMemo, useEffect, useState } from 'react';
import _get from 'lodash/get';
import {
  shouldShowBooking,
  LOGGING,
  DetailedProviderContent,
  PurposeOptionsByRelationship,
  Slot,
  FullAppointmentInfo
} from '@kyruus/provider-components';
import {
  Mloc,
  ProviderMatchProviderV9,
  PMCConfig,
  ProviderMatchProviderLocationV9
} from '@kyruus/types';
import ProviderMicrodata from '../ProviderMicrodata/provider-microdata';
import ProfileImage from '../ProfileImage/profile-image';
import ProfileNavigation from '../ProfileNavigation/profile-navigation';
import ProviderName from '../ProviderName/provider-name';
import { ProviderRatingsAndReviewsSummary } from '../ProviderRatingsAndReviewsSummary/provider-ratings-and-reviews-summary';
import ProviderSpecialties from '../ProviderSpecialties/provider-specialties';
import ProviderHighlights from '../ProviderHighlights/provider-highlights';
import ProviderAbout from '../ProviderAbout/provider-about';
import ProviderVideo from '../ProviderVideo/provider-video';
import ProviderLocations from '../ProviderLocations/provider-locations';
import ProviderClinicalInterests from '../ProviderClinicalInterests/provider-clinical-interests';
import ProviderExpertise from '../ProviderExpertise/provider-expertise';
import ProviderInsuranceAccepted from '../ProviderInsuranceAccepted/provider-insurance-accepted';
import ProviderReviews from '../ProviderReviews/provider-reviews';
import AlternateProviders from '../AlternateProviders/alternate-providers';
import { CustomXPFieldRenderer } from '../CustomXPFieldRenderer/custom-xp-field-renderer';
import { CareTeam } from '../CareTeam';
import {
  PROFILE_SECTION_IDS,
  BREAKPOINTS,
  DEFAULT_PROFILE_SECTIONS_ORDER
} from '../../lib/constants';
import { isDebugMode, getProviderPhone } from '../../lib/utils';
import { useMediaQuery, useProfileSections } from '../../lib/hooks';
import {
  TrackingFunctionContext,
  DebugErrorBoundaryContext
} from '../../lib/contexts';
import { BookingStrategies } from '../../types';
import { AvailabilityProps } from '../ScheduleAppointment/types';
import { getRenderableSections } from '../../lib/ordering';
import {
  ProfileContainer,
  Content,
  ProfileCenterTopContainer,
  ProfileCenterMeta,
  ProviderBadges,
  ProfileSectionWrapper,
  ShowContactInMobile,
  ShowContactInMobileWrapper,
  ProfileNavigationContainer,
  ProviderInfoWrapper
} from './styles';
import { AwsMapConfig } from '@kyruus/aws-map';
import ProfileContact from '../ProfileContact/profile-contact';
import { ScheduleAppointmentSection } from '../ScheduleAppointment/ScheduleAppointmentSection';
import { ScheduleAppointmentAside } from '../ScheduleAppointment/ScheduleAppointmentAside';
import { ScheduleAppointmentFixedMobileFooter } from '../ScheduleAppointment/ScheduleAppointmentFixedMobileFooter';
import { MyChartOpenSchedulingWidget } from '../OpenScheduling/OpenSchedulingWidget';
import { getBookingStrategy } from '../ScheduleAppointment/utilities';
import {
  shouldShowScheduleAppointmentsSectionOnMobile,
  isBookingEnabled
} from '../../utilities';
import { LoggingFunction } from '@kyruus/provider-components/provider-components/dist/ProviderAvailability/types';

export interface ProfileProps {
  /*
   * The patient relationship and appointment purpose for the provider's availability tiles.
   * This data indicates the user has searched by appointment, and will be used in displaying
   * the availability view
   */
  apptInfo?: FullAppointmentInfo;
  /**
   * A map of location id's to their availability. Used to determine which locations to render availability for.
   * If undefined, assume we are in a loading or error state and display all locations.
   */
  availabilityByLocationId?: {
    [key: string]: Array<any>;
  };
  /** Available Appointment purposes */
  purposeOptionsByRelationship: PurposeOptionsByRelationship;
  /** Function to return an Availability Tile component */
  availabilityTileFactory?: (
    location: ProviderMatchProviderLocationV9
  ) => React.ReactElement;
  /** Callback provided to the profile to handle users clicking the booking button */
  bookingCallback: (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => void;
  /*
   * The URL that the user will be directed to when booking.
   * Provides the `href` for buttons that will be handled by the callback,
   * and used when customer config has button be a direct link.
   * If this prop is defined, the booking CTA will render as an anchor tag (bookingCallback will still be called).
   */
  bookOnlineUrl?: string;
  /** The PMC config object, used for controlling certain behaviors of the profile */
  config: PMCConfig;
  /** Callback provided to the profile with new appointment info */
  editApptInfoCallback?: (apptInfo: FullAppointmentInfo) => void;
  /** The locations array for the provider */
  locations: ProviderMatchProviderLocationV9[];
  loading: {
    /** Whether or not the profile is loading booking info */
    bookingInfo: boolean;
  };
  /** The provider to display the profile of. */
  provider: ProviderMatchProviderV9;
  /** The availability_density_best value for this provider (https://kyruus.jira.com/wiki/spaces/AHC/pages/940441689/Feature+Availability+Density) */
  availabilityDensityBest?: string;
  /** The UTC date of the provider's soonest appointment */
  soonestAppointmentDate?: string;
  /** Track function for analytics */
  track: LoggingFunction;
  /** url to load google maps from (PMC will load the google maps api onto the window object and passing undefined for this value in PMC context will work fine) */
  googleMapsUrl?: string;
  /** Function to build a provider's location's url */
  getLocationUrl: (location: ProviderMatchProviderLocationV9) => string | null;
  /** Callback for when a provider's location anchor is clicked */
  locationCallback?: (locationUrl: string, isExternal: boolean) => void;
  getWaitTimeCallbackFunction: (
    locationEhrId: string,
    sourceSystem: string,
    bookAsTypeIds: string[]
  ) => void;
  waitTimeMap: Record<string, string>;
  /** Marketable locations for this provider */
  mlocations: { [key: string]: Mloc };
  altProviders: DetailedProviderContent[];
  careTeamProviders: DetailedProviderContent[];
  createAvailabilityUrl: () => string;
  // @todo: fix this type
  createProviderUrl: any;
  /** Whether or not to hide the anchors in the page (useful for when embedded in fixed height iframes that break the jump links, such as in CCF) */
  shouldHidePageAnchors: boolean;
  awsMapConfig: AwsMapConfig;
  fetchProviderAvailability: (
    providerId: number,
    appointmentInfo: FullAppointmentInfo
  ) => Promise<{ slots: Slot[]; error?: boolean }>;
  bookSrc: string;
  handleTimeSlotsClick: (
    event: React.SyntheticEvent,
    data: {
      providerId: string;
      date?: string;
      time?: string;
      href?: string;
      unformattedDate?: string;
      locationId?: string;
      patientRelationship?: string;
      [key: string]: unknown;
    }
  ) => void;
  handleViewMoreClick: (
    event: React.SyntheticEvent,
    data: {
      providerId?: string;
      viewMoreBookOnlineUrl?: string;
      [key: string]: unknown;
    }
  ) => void;
  availTilesBookOnlineUrl: string;
  // only applies when `darkship_feature_flags.use_cta_drawer` is enabled
  showBookingCtaButton: boolean;
}

const ProviderProfile = ({
  provider,
  config,
  locations,
  mlocations,
  availabilityDensityBest,
  soonestAppointmentDate,
  bookOnlineUrl,
  bookingCallback,
  googleMapsUrl,
  getLocationUrl,
  locationCallback,
  getWaitTimeCallbackFunction,
  waitTimeMap,
  loading: { bookingInfo: loadingBookingInfo },
  apptInfo,
  availabilityByLocationId,
  availabilityTileFactory,
  editApptInfoCallback,
  track,
  altProviders,
  careTeamProviders,
  createAvailabilityUrl,
  createProviderUrl,
  shouldHidePageAnchors = false,
  awsMapConfig,
  purposeOptionsByRelationship,
  fetchProviderAvailability,
  bookSrc,
  handleTimeSlotsClick,
  handleViewMoreClick,
  availTilesBookOnlineUrl,
  // only applies when `darkship_feature_flags.use_cta_drawer` is enabled
  showBookingCtaButton = false
}: ProfileProps): React.ReactElement => {
  // State to determine whether to render the highlights nav
  const [shouldRenderHighlightsNav, setShouldRenderHighlightsNav] =
    React.useState(true);

  // Callback to skip rendering the highlights nav if all highlights data is not present
  const handleHighlightsNavRender = () => {
    setShouldRenderHighlightsNav(false);
  };

  const renderableSections = useMemo(
    () =>
      config.provider_v9?.sections
        ? getRenderableSections({
            defaultSections: DEFAULT_PROFILE_SECTIONS_ORDER,
            sectionOrder: config.provider_v9?.sections.order,
            excludedSections: config.provider_v9?.sections.exclude,
            customXPFieldSections: config.provider_v9?.sections.custom
          })
        : DEFAULT_PROFILE_SECTIONS_ORDER,
    [config]
  );

  const { navItems, rendered } = useProfileSections({
    provider,
    config,
    locations,
    careTeamProviders,
    altProviders,
    shouldRenderHighlightsNav,
    renderableSections
  });

  const { autolink } = config.provider_v9 ?? {};

  const isProfileImageCircle =
    config.theme?.provider_profile_provider_image_border_radius === '50%';

  const handleBook = useCallback(
    (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      track(LOGGING.MESSAGE.REQUEST_APPOINTMENT, {
        provider_id: provider.id,
        button_location: 'profile'
      });
      track(LOGGING.MESSAGE.CALL_TO_ACTION, {
        provider_id: provider.id,
        placement: LOGGING.PLACEMENTS.PROVIDER_CARD,
        button_text: (e.target as HTMLButtonElement).innerText
      });

      bookingCallback(e);
    },
    [bookingCallback, provider.id, track]
  );

  const handleClickRatingReviewCount = useCallback(() => {
    track(LOGGING.MESSAGE.PROFILE_RATING_REVIEW_COUNT_CLICKED, {
      provider_id: provider.id
    });
  }, [provider.id, track]);

  const availability = {
    enabled: Boolean(apptInfo) && Boolean(editApptInfoCallback),
    availabilityByLocationId: availabilityByLocationId,
    availabilityTileFactory: availabilityTileFactory,
    onEditApptInfo: editApptInfoCallback,
    handleTimeSlotsClick,
    handleViewMoreClick
  } as AvailabilityProps;

  // If the booking section is displayed and the conditions to display availability tiles are met,
  // then on mobile screens the tiles are rendered in the center column of the UI.
  // Otherwise they are rendered in the right column.

  // Determine the appointment information configuration based on feature flags and default values.
  // If the `use_defaults_for_booking_questions` feature flag is disabled, reset the appointment info to an empty object.
  // Otherwise, if the `purpose` field is not defined in the appointment info, set a default purpose of 'in_person'.
  const bookingStrategy = getBookingStrategy(config, provider, availability);
  const isCtaDrawerEnabled = _get(
    config,
    'darkship_feature_flags.use_cta_drawer',
    false
  );
  // We hide the booking section entirely if there is nothing to populate it
  // TODO HSAP-2072 Remove ternary and just check isBookingEnabled when darkship_feature_flags.use_cta_drawer is removed
  const showBooking = isCtaDrawerEnabled
    ? isBookingEnabled({
        showBookingCtaButton,
        bookingStrategy
      })
    : shouldShowBooking(provider, config.modal_display || {});
  let appointmentInfoConfig = apptInfo;
  if (!config.feature_flags?.use_defaults_for_booking_questions) {
    appointmentInfoConfig = {};
  }
  if (
    config.feature_flags?.use_defaults_for_booking_questions &&
    !appointmentInfoConfig?.purpose
  ) {
    appointmentInfoConfig = {
      ...appointmentInfoConfig,
      purpose: 'in_person'
    };
  }

  availability.apptInfo = appointmentInfoConfig;

  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const isMobileOrTablet = useMediaQuery(`(max-width: ${BREAKPOINTS.large}px)`);

  const showScheduleAppointmentSection =
    showBooking &&
    shouldShowScheduleAppointmentsSectionOnMobile(bookingStrategy) &&
    isMobileOrTablet;

  const showScheduleAppointmentAside = showBooking && !isMobileOrTablet;

  // shouldHidePageAnchors is isCCF passing from PMC
  const showWaitTimeSection =
    _get(config, 'darkship_feature_flags.show_wait_time_link', false) &&
    !shouldHidePageAnchors;

  // There should be a separate flag checked here for enabling/disabling button visibility but
  // we need to do some rework on the feature flags for this section first
  const showWaitTimeButton =
    showWaitTimeSection && provider.external_wait_time_url;

  const showScheduleAppointmentFixedMobileFooter =
    showBooking &&
    bookingStrategy === BookingStrategies.Button &&
    isMobileOrTablet;

  // Extracted wait time section conditions
  const shouldShowWaitTimeOnMobile = showWaitTimeSection && isMobileOrTablet;

  const shouldShowWaitTimeOnDesktop = showWaitTimeSection && !isMobileOrTablet;

  // find phone number from provider contacts array
  const providerPhone = getProviderPhone(provider);

  function renderSection(sectionId: string) {
    switch (sectionId) {
      case PROFILE_SECTION_IDS.SCHEDULE_APPOINTMENTS:
        if (
          isMobileOrTablet &&
          (showWaitTimeSection || showScheduleAppointmentSection)
        ) {
          return (
            <ScheduleAppointmentSection
              key={sectionId}
              availability={availability}
              provider={provider}
              locations={locations}
              mlocations={mlocations}
              config={config}
              availabilityDensityBest={availabilityDensityBest}
              bookOnlineUrl={bookOnlineUrl}
              onBook={handleBook}
              purposeOptionsByRelationship={purposeOptionsByRelationship}
              track={track}
              loadingBookingInfo={loadingBookingInfo}
              fetchProviderAvailability={fetchProviderAvailability}
              bookSrc={bookSrc}
              availTilesBookOnlineUrl={availTilesBookOnlineUrl}
              showScheduleAppointmentSection={showScheduleAppointmentSection}
              showWaitTimeSection={shouldShowWaitTimeOnMobile}
              showWaitTimeButton={showWaitTimeButton}
              getWaitTimeCallbackFunction={getWaitTimeCallbackFunction}
              waitTimeMap={waitTimeMap}
            />
          );
        }

        if (!showScheduleAppointmentSection && providerPhone) {
          return (
            <ShowContactInMobileWrapper key={sectionId}>
              <ShowContactInMobile>
                <ProfileContact provider={provider} track={track} />
              </ShowContactInMobile>
            </ShowContactInMobileWrapper>
          );
        }
        return null;
      case PROFILE_SECTION_IDS.HIGHLIGHTS:
        return (
          rendered.highlightsSection && (
            <ProfileSectionWrapper
              key={sectionId}
              id={PROFILE_SECTION_IDS.HIGHLIGHTS}
              data-testid={PROFILE_SECTION_IDS.HIGHLIGHTS}
            >
              <ProviderHighlights
                provider={provider}
                config={config}
                bookOnlineUrl={bookOnlineUrl}
                hasAvailability={Boolean(soonestAppointmentDate)}
                shouldHidePageAnchors={shouldHidePageAnchors}
                onBook={handleBook}
                shouldSkipHighlightsNavRender={handleHighlightsNavRender}
              />
            </ProfileSectionWrapper>
          )
        );
      case PROFILE_SECTION_IDS.ABOUT:
        return (
          rendered.aboutSection && (
            <ProfileSectionWrapper
              key={sectionId}
              id={PROFILE_SECTION_IDS.ABOUT}
              data-testid={PROFILE_SECTION_IDS.ABOUT}
            >
              <ProviderAbout
                config={config}
                provider={provider}
                track={track}
                autolink={autolink}
              />
            </ProfileSectionWrapper>
          )
        );
      case PROFILE_SECTION_IDS.APPOINTMENTS:
        return (
          rendered.appointmentsSection && (
            <ProfileSectionWrapper
              key={sectionId}
              id={PROFILE_SECTION_IDS.APPOINTMENTS}
              data-testid={PROFILE_SECTION_IDS.APPOINTMENTS}
            >
              <MyChartOpenSchedulingWidget
                openSchedulingConfig={
                  config.third_party?.mychart_open_scheduling
                }
                provider={provider}
              />
            </ProfileSectionWrapper>
          )
        );
      case PROFILE_SECTION_IDS.VIDEOS:
        return (
          rendered.videoSection && (
            <ProfileSectionWrapper
              key={sectionId}
              id={PROFILE_SECTION_IDS.VIDEOS}
              data-testid={PROFILE_SECTION_IDS.VIDEOS}
            >
              <ProviderVideo provider={provider} />
            </ProfileSectionWrapper>
          )
        );
      case PROFILE_SECTION_IDS.LOCATIONS:
        return (
          rendered.locationsSection && (
            <ProfileSectionWrapper
              key={sectionId}
              id={PROFILE_SECTION_IDS.LOCATIONS}
              data-testid={PROFILE_SECTION_IDS.LOCATIONS}
            >
              <ProviderLocations
                locations={locations}
                getLocationUrl={getLocationUrl}
                locationCallback={locationCallback}
                googleMapsUrl={googleMapsUrl}
                mlocations={mlocations}
                track={track}
                config={config}
                awsMapConfig={awsMapConfig}
              />
            </ProfileSectionWrapper>
          )
        );
      case PROFILE_SECTION_IDS.EXPERTISE:
        return (
          rendered.expertiseSection && (
            <ProfileSectionWrapper
              key={sectionId}
              id={PROFILE_SECTION_IDS.EXPERTISE}
              data-testid={PROFILE_SECTION_IDS.EXPERTISE}
            >
              <ProviderExpertise provider={provider} track={track} />
            </ProfileSectionWrapper>
          )
        );
      case PROFILE_SECTION_IDS.INSURANCE_ACCEPTED:
        return (
          rendered.insuranceSection && (
            <ProfileSectionWrapper
              key={sectionId}
              id={PROFILE_SECTION_IDS.INSURANCE_ACCEPTED}
              data-testid={PROFILE_SECTION_IDS.INSURANCE_ACCEPTED}
            >
              <ProviderInsuranceAccepted
                provider={provider}
                config={config}
                track={track}
              />
            </ProfileSectionWrapper>
          )
        );
      case PROFILE_SECTION_IDS.REVIEWS:
        return (
          rendered.reviewsSection && (
            <ProfileSectionWrapper
              key={sectionId}
              id={PROFILE_SECTION_IDS.REVIEWS}
              data-testid={PROFILE_SECTION_IDS.REVIEWS}
            >
              <ProviderReviews provider={provider} track={track} />
            </ProfileSectionWrapper>
          )
        );
      case PROFILE_SECTION_IDS.CARE_TEAM:
        return (
          rendered.careTeamSection && (
            <ProfileSectionWrapper
              key={sectionId}
              id={PROFILE_SECTION_IDS.CARE_TEAM}
              data-testid={PROFILE_SECTION_IDS.CARE_TEAM}
            >
              <CareTeam
                careTeamProviders={careTeamProviders}
                createAvailabilityUrl={createAvailabilityUrl}
                createProviderUrl={createProviderUrl}
                track={track}
              />
            </ProfileSectionWrapper>
          )
        );
      case PROFILE_SECTION_IDS.SIMILAR_PROVIDERS:
        return (
          rendered.altProvidersSection && (
            <ProfileSectionWrapper
              key={sectionId}
              id={PROFILE_SECTION_IDS.SIMILAR_PROVIDERS}
              data-testid={PROFILE_SECTION_IDS.SIMILAR_PROVIDERS}
            >
              <AlternateProviders
                altProviders={altProviders}
                createAvailabilityUrl={createAvailabilityUrl}
                createProviderUrl={createProviderUrl}
                track={track}
              />
            </ProfileSectionWrapper>
          )
        );
      case PROFILE_SECTION_IDS.CLINICAL_INTERESTS:
        return (
          rendered.clinicalInterestsSection && (
            <ProfileSectionWrapper
              key={sectionId}
              id={PROFILE_SECTION_IDS.CLINICAL_INTERESTS}
              data-testid={PROFILE_SECTION_IDS.CLINICAL_INTERESTS}
            >
              <ProviderClinicalInterests provider={provider} track={track} />
            </ProfileSectionWrapper>
          )
        );
      default: {
        // must be a custom xp field
        const customXPFieldConfig = config.provider_v9?.sections?.custom?.find(
          (xpFieldConfig) => xpFieldConfig.id === sectionId
        );
        if (customXPFieldConfig) {
          // if we have an XP field config, check for data on the provider object
          const providerData = _get(provider, customXPFieldConfig.field, null);
          if (providerData) {
            // if we have provider data, render the content
            return (
              <ProfileSectionWrapper
                key={sectionId}
                id={customXPFieldConfig.id}
                data-testid={customXPFieldConfig.id}
              >
                <CustomXPFieldRenderer
                  customXPFieldConfig={customXPFieldConfig}
                  provider={provider}
                  autolink={autolink}
                />
              </ProfileSectionWrapper>
            );
          }
        }
      }
    }
    return null;
  }

  const providerItemType =
    provider.entity_type === 'location'
      ? 'PhysiciansOffice'
      : 'IndividualPhysician';

  return (
    <DebugErrorBoundaryContext.Provider value={isDebugMode()}>
      <TrackingFunctionContext.Provider value={track}>
        <ProfileContainer
          itemScope={true}
          itemType={`https://schema.org/${providerItemType}`}
          itemID={`#provider-${provider.id}`}
          id={PROFILE_SECTION_IDS.PROFILE_TOP}
          data-testid="ProviderProfile"
        >
          <ProviderMicrodata provider={provider} />
          <meta itemProp="url" content={createProviderUrl(provider)} />
          <ProfileCenterTopContainer>
            <ProfileImage provider={provider} isCircle={isProfileImageCircle} />
            <ProfileCenterMeta>
              <ProviderInfoWrapper>
                <ProviderName provider={provider} />
                <ProviderRatingsAndReviewsSummary
                  shouldHidePageAnchors={shouldHidePageAnchors}
                  provider={provider}
                  ratingReviewCountHref={`#${PROFILE_SECTION_IDS.REVIEWS}`}
                  handleClickRatingReviewCount={handleClickRatingReviewCount}
                />
              </ProviderInfoWrapper>
              <ProviderSpecialties provider={provider} />
              <ProviderBadges config={config} provider={provider} />
            </ProfileCenterMeta>
          </ProfileCenterTopContainer>
          {!shouldHidePageAnchors && !isMobileOrTablet && (
            <ProfileNavigationContainer className="profile-navigation-container">
              <ProfileNavigation navOptions={navItems} track={track} />
            </ProfileNavigationContainer>
          )}
          <Content
            data-testid="ProviderProfileContent"
            id="profile-main-content"
          >
            {renderableSections.map((sectionName: string) =>
              renderSection(sectionName)
            )}
          </Content>

          {isClient &&
            !isMobileOrTablet &&
            (showWaitTimeSection || showScheduleAppointmentAside) && (
              <ScheduleAppointmentAside
                availability={availability}
                provider={provider}
                locations={locations}
                mlocations={mlocations}
                config={config}
                availabilityDensityBest={availabilityDensityBest}
                bookOnlineUrl={bookOnlineUrl}
                onBook={handleBook}
                purposeOptionsByRelationship={purposeOptionsByRelationship}
                track={track}
                loadingBookingInfo={loadingBookingInfo}
                fetchProviderAvailability={fetchProviderAvailability}
                bookSrc={bookSrc}
                availTilesBookOnlineUrl={availTilesBookOnlineUrl}
                showScheduleAppointmentAside={showScheduleAppointmentAside}
                showWaitTimeSection={shouldShowWaitTimeOnDesktop}
                showWaitTimeButton={showWaitTimeButton}
                getWaitTimeCallbackFunction={getWaitTimeCallbackFunction}
                waitTimeMap={waitTimeMap}
              />
            )}

          {isClient && showScheduleAppointmentFixedMobileFooter && (
            <ScheduleAppointmentFixedMobileFooter
              provider={provider}
              config={config}
              bookOnlineUrl={bookOnlineUrl}
              onBook={handleBook}
              availabilityDensityBest={availabilityDensityBest}
              loadingBookingInfo={loadingBookingInfo}
            />
          )}
        </ProfileContainer>
      </TrackingFunctionContext.Provider>
    </DebugErrorBoundaryContext.Provider>
  );
};

export default ProviderProfile;
